/**
 * PATH: packages/email-hub/src/feature/reader-header/placements/primaryOverride.ts
 * FILE_ID: EMAILHUB-READERHEADER-PLACEMENTS-PRIMARY-OVERRIDE-001
 * PURPOSE: Persist the operator's manual "designate primary placement" choice.
 * ROLE IN ARCHITECTURE: Tiny local preference store + subscription for the
 *   reader placements control and manager.
 * MAIN EXPORTS: getPrimaryOverride, setPrimaryOverride, usePrimaryOverride.
 * NON-RESPONSIBILITIES: Primary ORDERING policy (model/placements.ts owns it);
 *   server persistence (no backend field exists — see scoping note).
 * NOTES FOR FUTURE AI: DISCOVERED SCOPE — the allocation API has no
 *   "primary" field, so this is a LOCAL PER-USER preference: localStorage is
 *   per browser profile, which on managed operator machines approximates
 *   per-user per-device. It deliberately does NOT sync across devices or
 *   users. When the backend grows an is_primary field, migrate reads to the
 *   payload and keep this store as the offline fallback only.
 */
import { useSyncExternalStore } from 'react'

const KEY_PREFIX = 'qt-reader-primary-placement:'

type Listener = () => void
const listeners = new Set<Listener>()

function storageKey(emailId: string): string {
  return `${KEY_PREFIX}${emailId}`
}

function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    // Storage access can throw in privacy modes — treat as "no preference".
    return null
  }
}

/** The manually designated primary allocation_id, or null when unset. */
export function getPrimaryOverride(emailId: string): string | null {
  const storage = safeStorage()
  if (!storage) return null
  return storage.getItem(storageKey(emailId))
}

/** Set (allocationId) or clear (null) the designation, notifying subscribers. */
export function setPrimaryOverride(
  emailId: string,
  allocationId: string | null,
): void {
  const storage = safeStorage()
  if (storage) {
    if (allocationId) storage.setItem(storageKey(emailId), allocationId)
    else storage.removeItem(storageKey(emailId))
  }
  for (const listener of listeners) listener()
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Reactive read — re-renders the caller when the designation changes. */
export function usePrimaryOverride(emailId: string): string | null {
  return useSyncExternalStore(
    subscribe,
    () => getPrimaryOverride(emailId),
    () => null,
  )
}
